import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, ShieldCheck, Pencil } from "lucide-react";
import { toast } from "sonner";
import { API, ROLES, ROLE_DESCRIPTIONS } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const ROLE_BADGE = {
  admin: "bg-indigo-100 text-indigo-700 hover:bg-indigo-100",
  accountant: "bg-violet-100 text-violet-700 hover:bg-violet-100",
  sales: "bg-blue-100 text-blue-700 hover:bg-blue-100",
  stock_team: "bg-teal-100 text-teal-700 hover:bg-teal-100",
  stock_head: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100",
  staff: "bg-slate-100 text-slate-600 hover:bg-slate-100",
};

const emptyMember = { name: "", email: "", password: "", role: "sales" };

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyMember);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ id: "", name: "", email: "", password: "", role: "sales" });

  const load = useCallback(async () => {
    const { data } = await API.get("/users");
    setUsers(data);
  }, []);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const create = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password) return toast.error("All fields are required");
    try {
      await API.post("/users", form);
      setOpen(false);
      setForm(emptyMember);
      toast.success("Team member added");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const openEdit = (u) => {
    setEditForm({ id: u.id, name: u.name, email: u.email, password: "", role: u.role });
    setEditOpen(true);
  };

  const update = async () => {
    if (!editForm.name.trim() || !editForm.email.trim()) return toast.error("Name and email are required");
    try {
      const payload = { name: editForm.name, email: editForm.email, role: editForm.role };
      if (editForm.password) payload.password = editForm.password;
      await API.patch(`/users/${editForm.id}`, payload);
      setEditOpen(false);
      toast.success("Member updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    try {
      await API.delete(`/users/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="flex flex-col items-start gap-3 rounded-lg border border-slate-200 bg-white p-10" data-testid="team-no-access">
        <ShieldCheck className="h-8 w-8 text-slate-400" />
        <div className="text-lg font-semibold text-slate-900">Admin access required</div>
        <div className="text-sm text-slate-500">Only the admin can manage team members.</div>
      </div>
    );
  }

  const roleSelect = (value, onChange, testid) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="mt-1.5" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="admin">Admin — full access, manages users</SelectItem>
        <SelectItem value="accountant">Accountant — full B2B books & challans</SelectItem>
        <SelectItem value="sales">Sales — CRM & quotes, view stock only</SelectItem>
        <SelectItem value="stock_team">Stock Team — inbound/outbound only</SelectItem>
        <SelectItem value="stock_head">Stock Head — all stock settings & purchase rates</SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Team</h1>
          <p className="text-sm text-slate-500">Add staff logins so they can use CRM, tasks and inventory</p>
        </div>
        <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setOpen(true)} data-testid="add-member-button">
          <Plus className="mr-1 h-4 w-4" /> Add Member
        </Button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email (Member ID)</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="team-table">
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`team-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <Badge className={ROLE_BADGE[u.role] || ROLE_BADGE.staff}>{ROLES[u.role] || u.role}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`edit-member-${u.id}`} aria-label="Edit member">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {u.id !== user?.id && (
                    <Button variant="ghost" size="icon" onClick={() => remove(u.id)} data-testid={`remove-member-${u.id}`} aria-label="Remove member">
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="member-dialog">
          <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="member-name-input" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="member-email-input" />
            </div>
            <div>
              <Label>Password *</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Share this with your staff" className="mt-1.5" data-testid="member-password-input" />
            </div>
            <div>
              <Label>Role</Label>
              {roleSelect(form.role, (v) => setForm({ ...form, role: v }), "member-role-select")}
              <p className="mt-1.5 text-xs text-slate-500" data-testid="role-description">{ROLE_DESCRIPTIONS[form.role]}</p>
            </div>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={create} data-testid="save-member-button">Add Member</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-member-dialog">
          <DialogHeader><DialogTitle>Edit Member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1.5" data-testid="edit-member-name-input" />
            </div>
            <div>
              <Label>Email (Member ID) *</Label>
              <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="mt-1.5" data-testid="edit-member-email-input" />
            </div>
            <div>
              <Label>New Password</Label>
              <Input type="text" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Leave blank to keep current password" className="mt-1.5" data-testid="edit-member-password-input" />
            </div>
            <div>
              <Label>Role</Label>
              {roleSelect(editForm.role, (v) => setEditForm({ ...editForm, role: v }), "edit-member-role-select")}
            </div>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={update} data-testid="update-member-button">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
